import { Action, Reducer } from 'redux';
import { AppThunkAction } from '..';
import { AuthenticateResponse } from '../../Interfaces/AuthenticateResponse';
import { AuthenticateRequest } from '../../Interfaces/AuthenticationRequest';
import { postRequestOptions } from '../../utils/utils';

export enum UserSessionActionTypes {
  LOGGING_IN = 'LOGGING_IN',
  ON_LOGIN_SUCCESSFUL = 'ON_LOGIN_SUCCESSFUL',
  ON_LOGIN_FAILED = 'ON_LOGIN_FAILED',
  ON_LOGOUT = 'ON_LOGOUT',
}
// -----------------
// STATE - This defines the type of data maintained in the Redux store.
export interface UserSessionState {
  sessionInfo: AuthenticateResponse | undefined;
  isLoggedIn: boolean;
  loginFailed: boolean;
  loginFailedMessage: string;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
interface LoggingInAction {
  type: UserSessionActionTypes.LOGGING_IN;
}

interface OnLoginSuccessfulAction {
  type: UserSessionActionTypes.ON_LOGIN_SUCCESSFUL;
  sessionInfo: AuthenticateResponse;
}

interface OnLoginFailedAction {
  type: UserSessionActionTypes.ON_LOGIN_FAILED;
  message: string;
}

interface OnLogoutAction {
  type: UserSessionActionTypes.ON_LOGOUT;
  sessionInfo: AuthenticateResponse | undefined;
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | LoggingInAction
  | OnLoginSuccessfulAction
  | OnLoginFailedAction
  | OnLogoutAction;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
export const actionCreators = {
  onLogin:
    (
      credentials: AuthenticateRequest | undefined,
      idToken: any | undefined,
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log('Logging in');
      try {
        dispatch({
          type: UserSessionActionTypes.LOGGING_IN,
        });
        let response: Response = await fetch(
          'usersession/login',
          postRequestOptions(appState.userSessionReducer.sessionInfo?.Token, {
            authenticateRequest: credentials,
            token: idToken,
          }),
        );
        if (response.ok) {
          const result: AuthenticateResponse =
            (await response.json()) as AuthenticateResponse;

          if (result.Token !== '') {
            dispatch({
              type: UserSessionActionTypes.ON_LOGIN_SUCCESSFUL,
              sessionInfo: result,
            });
            console.log(`Logged in: ${result.FirstName} ${result.LastName}`);
            return;
          }
        } else {
          const errorBody = await response.json();
          dispatch({
            type: UserSessionActionTypes.ON_LOGIN_FAILED,
            message: errorBody.message,
          });
          return;
        }
      } catch (e) {
        console.log('Login failed');
      }
      dispatch({
        type: UserSessionActionTypes.ON_LOGIN_FAILED,
        message: 'LOGIN_ERROR_MSG',
      });
    },

  onLogout: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
    const appState = getState();
    if (!appState) return;

    console.log(
      `Logging out: ${appState.userSessionReducer.sessionInfo?.FirstName} ${appState.userSessionReducer.sessionInfo?.LastName}`,
    );

    await fetch(
      'usersession/logout',
      postRequestOptions(
        appState.userSessionReducer.sessionInfo?.Token,
        appState.userSessionReducer.sessionInfo,
      ),
    );

    dispatch({
      type: UserSessionActionTypes.ON_LOGOUT,
      sessionInfo: undefined,
    });
    console.log('Logged out');
  },
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const unloadedState: UserSessionState = {
  sessionInfo: undefined,
  isLoggedIn: false,
  loginFailed: false,
  loginFailedMessage: '',
};

export const reducer: Reducer<UserSessionState> = (
  state: UserSessionState | undefined,
  incomingAction: Action,
): UserSessionState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case UserSessionActionTypes.LOGGING_IN:
      return {
        ...state,
        isLoggedIn: false,
        loginFailed: false,
        loginFailedMessage: '',
      };
    case UserSessionActionTypes.ON_LOGIN_SUCCESSFUL:
      return {
        ...state,
        sessionInfo: action.sessionInfo,
        isLoggedIn: true,
        loginFailed: false,
        loginFailedMessage: '',
      };
    case UserSessionActionTypes.ON_LOGIN_FAILED:
      return {
        ...state,
        loginFailed: true,
        loginFailedMessage: action.message,
      };
    case UserSessionActionTypes.ON_LOGOUT:
      return unloadedState;
    default:
      break;
  }

  return state;
};
